import { useState, useEffect } from 'preact/hooks';
import { MessageSquare, UserPlus } from 'lucide-preact';
import { AppShell } from './components/layout/AppShell';
import { ContactSidebar } from './components/contacts/ContactSidebar';
import { Header } from './components/layout/Header';
import { WorkspaceTabs } from './components/panels/WorkspaceTabs';
import { ChatContainer } from './components/chat/ChatContainer';
import { SmartRepliesStream } from './components/chat/SmartRepliesStream';
import { ActionsTab } from './components/panels/ActionsTab';
import { SummariesTab } from './components/panels/SummariesTab';
import { ResizableDrawer } from './components/layout/ResizableDrawer';
import { AddContactModal } from './components/contacts/AddContactModal';
import { SummaryRangeModal } from './components/modals/SummaryRangeModal';
import { VisionModal } from './components/modals/VisionModal';
import { ContactContextMenu } from './components/contacts/ContactContextMenu';
import { DeleteContactModal } from './components/modals/DeleteContactModal';
import { KanbanBoardModal } from './components/modals/KanbanBoardModal';
import { TaskDetailModal } from './components/modals/TaskDetailModal';
import { NavigationOverlay } from './components/modals/NavigationOverlay';

import type { Contact } from './types/contact';
import { activeContactSignal, loadContacts, selectContact } from './state/contacts.store';
import { activeTabSignal, isAddContactModalOpenSignal } from './state/ui.store';
import { initRealtimeSync } from './services/sse';
import { tokenSignal } from './state/auth.store';
import { initRouter } from './services/router';
import { initStructuredCopyService } from './services/structuredCopyService';
import { initIosPwaViewport } from './services/iosPwaViewport';

export function App() {
  const activeContact = activeContactSignal.value;
  const activeTab = activeTabSignal.value;
  const isAuthenticated = Boolean(tokenSignal.value);

  const [isSummaryModalOpen, setIsSummaryModalOpen] = useState(false);
  const [visionMediaUrl, setVisionMediaUrl] = useState<string | null>(null);
  const [visionContent, setVisionContent] = useState('');
  const [visionMessageId, setVisionMessageId] = useState<string | undefined>(undefined);

  // Context Menu & Delete Modal State
  const [contextMenuState, setContextMenuState] = useState<{ x: number; y: number; contact: Contact } | null>(null);
  const [deleteModalContact, setDeleteModalContact] = useState<Contact | null>(null);

  useEffect(() => {
    initRouter();
    initStructuredCopyService();
    const cleanupViewport = initIosPwaViewport();

    if (isAuthenticated) {
      loadContacts();
      initRealtimeSync();
    }

    return () => {
      cleanupViewport();
    };
  }, [isAuthenticated]);

  const handleContextMenu = (e: MouseEvent | TouchEvent, contact: Contact) => {
    let clientX = 100;
    let clientY = 100;

    if ('clientX' in e) {
      clientX = e.clientX;
      clientY = e.clientY;
    } else if ('touches' in e && e.touches.length > 0) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    }

    setContextMenuState({ x: clientX, y: clientY, contact });
  };

  return (
    <AppShell>
      {/* Sidebar Contacts List — Full width on mobile when no contact selected, hidden on mobile when contact is active */}
      <div class={`h-full flex-shrink-0 ${activeContact ? 'hidden md:flex md:w-80' : 'w-full md:w-80 flex'}`}>
        <ContactSidebar
          onContextMenu={(e, c) => handleContextMenu(e, c)}
          onLongPress={(e, c) => handleContextMenu(e, c)}
          onRequestDelete={(c) => setDeleteModalContact(c)}
        />
      </div>

      {/* Main Workspace — Hidden on mobile when no contact selected, full width on mobile when contact is active */}
      <main class={`flex-1 flex-col min-w-0 bg-slate-950 h-full ${!activeContact ? 'hidden md:flex' : 'flex w-full'}`}>
        {!activeContact ? (
          /* Empty State Desktop */
          <div class="flex-1 flex flex-col items-center justify-center p-8 text-center">
            <div class="w-16 h-16 rounded-3xl bg-slate-900 border border-slate-800 flex items-center justify-center text-slate-400 mb-4">
              <MessageSquare class="w-8 h-8 text-cyan-400" />
            </div>
            <h3 class="text-lg font-bold text-slate-50 mb-1">Nenhum Contato Selecionado</h3>
            <p class="text-xs text-slate-300 max-w-sm leading-relaxed">
              Selecione um contato na barra lateral ou escolha um contato da sua lista do WhatsApp para monitorar.
            </p>
            <button
              onClick={() => isAddContactModalOpenSignal.value = true}
              class="mt-6 px-5 py-2.5 bg-cyan-600 hover:bg-cyan-500 text-white text-xs font-bold rounded-xl shadow-lg shadow-cyan-600/20 transition flex items-center gap-2 cursor-pointer min-h-[44px]"
            >
              <UserPlus class="w-4 h-4" /> Selecionar do WhatsApp
            </button>
          </div>
        ) : (
          /* Active Contact Workspace */
          <div class="flex-1 flex flex-col h-full overflow-hidden">
            {/* Header with Mobile Back Button */}
            <Header onOpenSummaryRangeModal={() => setIsSummaryModalOpen(true)} />

            {/* Workspace Tabs (Messages / Actions / Summaries) */}
            <WorkspaceTabs />

            {/* Content Container */}
            <div class="flex-1 flex min-h-0 overflow-hidden relative">
              {activeTab === 'tab-messages' && (
                <div class="flex-1 flex flex-col min-h-0">
                  <ChatContainer
                    onOpenVisionModal={(url, content, msgId) => {
                      setVisionMediaUrl(url);
                      setVisionContent(content);
                      setVisionMessageId(msgId);
                    }}
                  />
                  <div class="p-2 sm:p-3 border-t border-slate-800/80 shrink-0 pb-[max(0.75rem,env(safe-area-inset-bottom))] bg-slate-950/90 backdrop-blur-md">
                    <SmartRepliesStream />
                  </div>
                </div>
              )}

              {activeTab === 'tab-actions' && <ActionsTab />}

              {activeTab === 'tab-summaries' && <SummariesTab />}

              {/* AI Chat Drawer (Overlay on Mobile, Resizable on Desktop) */}
              <ResizableDrawer />
            </div>
          </div>
        )}
      </main>

      {/* Modals */}
      <AddContactModal />

      <SummaryRangeModal
        isOpen={isSummaryModalOpen}
        onClose={() => setIsSummaryModalOpen(false)}
      />

      <VisionModal
        mediaUrl={visionMediaUrl}
        content={visionContent}
        messageId={visionMessageId}
        onClose={() => {
          setVisionMediaUrl(null);
          setVisionMessageId(undefined);
        }}
      />

      {/* Contact Context Menu Popup (Web Right-Click & Mobile Long Press) */}
      {contextMenuState && (
        <ContactContextMenu
          x={contextMenuState.x}
          y={contextMenuState.y}
          contact={contextMenuState.contact}
          onClose={() => setContextMenuState(null)}
          onSelect={() => selectContact(contextMenuState.contact)}
          onDelete={() => setDeleteModalContact(contextMenuState.contact)}
        />
      )}

      {/* Delete Contact Confirmation Modal */}
      <DeleteContactModal
        isOpen={Boolean(deleteModalContact)}
        contact={deleteModalContact}
        onClose={() => setDeleteModalContact(null)}
      />

      {/* Global Kanban Task Management Modal & Detail Modal */}
      <KanbanBoardModal />
      <TaskDetailModal />
      <NavigationOverlay />
    </AppShell>
  );
}
